import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { resolveBoothInvite } from '../../services/booths';
import { useAuth } from '../../contexts/AuthContext';
import { formatAvailabilityDate } from '../../utils/availability';

export default function QueueInviteResolver() {
  // /queue/<booth> or /queue/<booth>/<event>; links sent before Sprint 7 use ?event=<event>.
  // /queue/<booth>/test/<code> is the booth's test link: it ignores the booth's open, close
  // and expiry times so the booth can be tried before the fair.
  const { inviteSlug, eventCode, testCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [eventSlug, setEventSlug] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [registerPath, setRegisterPath] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function run() {
      try {
        setLoading(true);
        setError('');
        setEventSlug('');
        setIsRegistered(false);
        setRegisterPath('');

        const eventParam = eventCode || new URLSearchParams(location.search).get('event') || '';
        // Store the link as opened so login can return to the same event-specific link
        const pendingInviteKey = `${location.pathname.replace(/^\/queue\//, '')}${location.search || ''}`;
        const invitePath = `/queue/${pendingInviteKey}`;

        // Step 1: Check if user is logged in
        if (!user) {
          localStorage.setItem('pendingQueueInvite', pendingInviteKey);
          navigate('/login', { 
            state: { 
              from: invitePath,
              message: 'Please log in to join the booth queue'
            } 
          });
          return;
        }
        
        // Step 2: Resolve the booth invite (optionally for a specific event)
        const data = await resolveBoothInvite(inviteSlug, {
          ...(eventParam ? { event: eventParam } : {}),
          ...(testCode ? { test: testCode } : {})
        });
        console.log('Invite resolve response:', data);
        
        const boothId = data?.boothId || data?.booth?._id;
        const event = data?.event;
        const resolvedEventSlug = event?.slug || eventParam || '';
        const registered = data?.isRegistered || false;
        const isEventUpcoming = data?.isEventUpcoming || false;
        const canJoinQueue = data?.canJoinQueue || false;
        const isBoothExpired = data?.isBoothExpired || false;
        const boothExpiredAt = data?.boothExpiredAt;
        const isBoothClosed = data?.isBoothClosed || false;
        const boothClosedAt = data?.boothClosedAt;
        const isBoothNotOpen = data?.isBoothNotOpen || false;
        // Only true when the server accepted the test code
        const isTestLink = !!testCode && data?.isTestLink === true;
        const employerPagePath = boothId && resolvedEventSlug
          ? `/booth-queue/${encodeURIComponent(resolvedEventSlug)}/${encodeURIComponent(boothId)}`
          : '';

        if (!cancelled) {
          setEventSlug(resolvedEventSlug);
          setIsRegistered(registered);
        }
        
        console.log('Extracted boothId:', boothId);
        console.log('Extracted eventSlug:', resolvedEventSlug);
        console.log('Is registered:', registered);
        console.log('Is event upcoming:', isEventUpcoming);
        console.log('Can join queue:', canJoinQueue);
        console.log('Is booth expired:', isBoothExpired, 'Expired at:', boothExpiredAt);
        console.log('Is booth closed:', isBoothClosed, 'Closed at:', boothClosedAt);
        
        // Step 3: Check if event is assigned to booth
        if (!event || !resolvedEventSlug) {
          setError('This booth is not assigned to any event. You are unable to join this booth.');
          setLoading(false);
          return;
        }

        // Step 3b: Check if booth link has expired (a test link ignores the booth's times)
        if (!isTestLink && isBoothExpired && boothExpiredAt) {
          setError(`This booth link expired on ${formatAvailabilityDate(boothExpiredAt)}. You are unable to join this booth.`);
          setLoading(false);
          return;
        }

        // Step 3c: Check if the booth has passed its close time
        if (!isTestLink && isBoothClosed && boothClosedAt) {
          setError(`This booth closed on ${formatAvailabilityDate(boothClosedAt)}. You are unable to join this booth or leave a message.`);
          setLoading(false);
          return;
        }

        const eventHasEnded = event?.end ? new Date(event.end) < new Date() : false;

        // Step 4: Check if user is registered for the event
        if (!registered && isTestLink) {
          // The employer page would show the booth's real (closed or not open) state, so ask
          // the tester to register and then open the test link again.
          setRegisterPath(`/event/${encodeURIComponent(resolvedEventSlug)}/register`);
          setError(`This is a test link for ${data?.booth?.name || 'this booth'}. Register for ${event?.name || 'the event'} first, then open this test link again to join the test queue.`);
          setLoading(false);
          return;
        }
        if (!registered) {
          // Step 5: Not registered — show the employer page, which offers
          // "Register for this event" (Sprint 7 "Not registered" state)
          if (employerPagePath && (isEventUpcoming || !eventHasEnded)) {
            navigate(employerPagePath, { replace: true });
            return;
          }
          if (isEventUpcoming) {
            // Navigate to event registration page
            navigate(`/event/${encodeURIComponent(resolvedEventSlug)}/register`, { replace: true });
            return;
          } else {
            // Event is not upcoming, show error
            setError('You are unable to join this booth. Please register for the event first.');
            setLoading(false);
            return;
          }
        }

        // Step 5b: Registered but the booth has not opened yet — the employer page
        // shows "Booth not open" and offers the queue when it opens
        if (!isTestLink && isBoothNotOpen && employerPagePath) {
          navigate(employerPagePath, { replace: true });
          return;
        }

        // Step 6: User is registered - check if they can join queue
        if (!canJoinQueue) {
          // Provide more specific error message based on event status
          const eventStatus = event?.status;
          const hasEnded = eventHasEnded;

          if (isBoothExpired) {
            // This shouldn't happen since we check above, but just in case
            const formattedDate = formatAvailabilityDate(boothExpiredAt) || 'an earlier date';
            setError(`This booth link expired on ${formattedDate}. You are unable to join this booth.`);
          } else if (isBoothClosed) {
            const formattedDate = formatAvailabilityDate(boothClosedAt) || 'an earlier date';
            setError(`This booth closed on ${formattedDate}. You are unable to join this booth or leave a message.`);
          } else if (hasEnded) {
            setError('This event has ended. You are unable to join this booth.');
          } else if (eventStatus !== 'published' && eventStatus !== 'active') {
            setError('This event is not currently available. You are unable to join this booth.');
          } else {
            setError('You are unable to join this booth at this time. Please contact support if you believe this is an error.');
          }
          setLoading(false);
          return;
        }

        // Step 7: User is registered and can join - navigate to queue entry
        if (!boothId || !resolvedEventSlug) {
          setError('Invalid or expired invite link.');
          setLoading(false);
          return;
        }
        
        // The entry form passes the test code on to the join request
        const testQuery = isTestLink ? `?test=${encodeURIComponent(testCode)}` : '';
        const targetUrl = `/booth-queue/${encodeURIComponent(resolvedEventSlug)}/${encodeURIComponent(boothId)}/entry${testQuery}`;
        console.log('Navigating to:', targetUrl);
        
        navigate(targetUrl, { replace: true });
      } catch (e) {
        if (!cancelled) {
          console.error('QueueInviteResolver error:', e);
          const errorMsg = e?.response?.data?.message || e?.message || 'Failed to open queue link.';
          setError(errorMsg);
          // Keep any event slug from the invite URL so Back can still go to the event page
          const fallbackEvent = eventCode || new URLSearchParams(location.search).get('event') || '';
          if (fallbackEvent) setEventSlug(fallbackEvent);
          setLoading(false);
        }
      }
    }
    run();
    return () => { cancelled = true; };
  }, [inviteSlug, eventCode, testCode, location.pathname, location.search, navigate, user]);

  const goToEventPage = () => {
    if (eventSlug) {
      const path = isRegistered
        ? `/events/registered/${encodeURIComponent(eventSlug)}`
        : `/event/${encodeURIComponent(eventSlug)}`;
      navigate(path);
      return;
    }
    navigate('/events/registered');
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
      <h2>{testCode ? 'Opening Test Queue…' : 'Opening Queue…'}</h2>
      {loading && !error ? (
        <p>Please wait while we verify your registration and take you to the booth queue.</p>
      ) : error ? (
        <div className="alert-box" role="alert" style={{ 
          background: '#ffe8e8', 
          borderColor: '#f5c2c7', 
          color: '#842029',
          padding: '1rem',
          borderRadius: '8px',
          marginTop: '1rem'
        }}>
          <p style={{ margin: '0 0 1rem 0', fontWeight: 500 }}>{error}</p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {registerPath && (
              <button
                className="dashboard-button"
                style={{ width: 'auto', marginTop: '0' }}
                onClick={() => navigate(registerPath)}
              >
                Register for Event
              </button>
            )}
            <button 
              className="dashboard-button" 
              style={{ width: 'auto', marginTop: '0' }} 
              onClick={goToEventPage}
            >
              {eventSlug ? 'Back to Event' : 'Back to Events'}
            </button>
            <button 
              className="dashboard-button" 
              style={{ width: 'auto', marginTop: '0' }} 
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
